import { FormattedMessage } from 'react-intl';

import { Button } from '@xing-com/button';
import { useLocation } from '@xing-com/crate-core-router';
import {
  useEditSearchAlert,
  useSearchStateFilledJobPreferences,
  useToggleSearchAlert,
  useUpdateJobPreferences,
} from '@xing-com/crate-jobs-hooks';
import type { UpdateJobPreferencesMutationParams } from '@xing-com/crate-jobs-hooks/src/search-alerts/types';
import type { FilledPreferences } from '@xing-com/crate-jobs-hooks/src/search-alerts/use-search-state-filled-job-preferences';
import {
  getNwtCleanQuery,
  useJobsNwtTracking,
} from '@xing-com/crate-jobs-tracking';
import type { SearchAlertsJobPreferencesBannerProps } from '@xing-com/crate-jobs-xinglets';

import { SEARCH_ALERT_JOB_PREFERENCES_LABEL_ID } from '../constants';

import { Toggle } from './job-preferences-search-alert-banner-action.styles';

const DEFAULT_DELETE_ARGS = {
  careerLevelPreferenceInput: {
    maximum: null,
    minimum: null,
  },
  disciplinesPreferenceInput: {
    disciplineIds: [],
  },
  industriesPreferenceInput: {
    industries: [],
    openToOtherIndustries: false,
  },
  jobTitlesTargetPositionsInput: {
    targetPositions: [],
  },
  locationsPreferenceInput: {
    cities: [],
    maxDistance: null,
  },
  salaryExpectationsPreferenceInput: {
    salaryExpectations: null,
  },
  workingHoursPreference: {
    fullTime: false,
    partTime: false,
  },
  workplacePreferenceInput: {
    homeOffice: false,
    mostlyHome: false,
    office: false,
    partlyHome: false,
  },
};

const getFillPreferencesInput = (
  filledPreferences: FilledPreferences
): UpdateJobPreferencesMutationParams => ({
  careerLevelPreferenceInput: {
    maximum: filledPreferences.maxCareerLevel,
    minimum: filledPreferences.minCareerLevel,
  },
  disciplinesPreferenceInput: {
    disciplineIds: filledPreferences.disciplines || [],
  },
  industriesPreferenceInput: {
    industries: filledPreferences.industries || [],
    openToOtherIndustries: filledPreferences.openToOtherIndustries,
  },
  jobTitlesTargetPositionsInput: {
    targetPositions: filledPreferences.jobTitle
      ? [filledPreferences.jobTitle]
      : [],
  },
  locationsPreferenceInput: {
    cities: filledPreferences.location?.cityId
      ? [filledPreferences.location.cityId]
      : [],
    maxDistance: filledPreferences.location?.radius,
  },
  salaryExpectationsPreferenceInput: {
    salaryExpectations: filledPreferences.salary?.min,
  },
  workingHoursPreference: {
    fullTime: filledPreferences.employmentTypes?.fullTime || false,
    partTime: filledPreferences.employmentTypes?.partTime || false,
  },
  workplacePreferenceInput: {
    homeOffice: filledPreferences.remoteOption?.homeOffice || false,
    mostlyHome: filledPreferences.remoteOption?.mostlyHome || false,
    office: filledPreferences.remoteOption?.office || false,
    partlyHome: filledPreferences.remoteOption?.partlyHome || false,
  },
});

const isValidResponse = (
  response: Parameters<
    NonNullable<UpdateJobPreferencesMutationParams['onCompleted']>
  >[0]
): boolean =>
  response.careerLevelPreference?.__typename ===
    'UpdateCareerLevelPreferenceSuccess' ||
  response.disciplinesPreference?.__typename ===
    'UpdateDisciplinesPreferenceSuccess' ||
  response.industriesPreference?.__typename ===
    'UpdateIndustriesPreferenceSuccess' ||
  response.jobTitlesTargetPositions?.__typename ===
    'UpdateJobTitlesTargetPositionsPreferenceSuccess' ||
  response.locationsPreference?.__typename ===
    'UpdateLocationsPreferenceSuccess' ||
  response.salaryExpectationsPreference?.__typename ===
    'UpdateSalaryExpectationsPreferenceSuccess' ||
  response.workingHoursPreference?.__typename ===
    'UpdateWorkingHoursPreferenceSuccess' ||
  response.workplacePreference?.__typename ===
    'UpdateWorkplacePreferenceSuccess';

export const SearchAlertBannerAction: React.FC<
  SearchAlertsJobPreferencesBannerProps
> = ({ itemCount, trackingToken }) => {
  const { isAlertActive, isLoading, onToggleAlert } = useToggleSearchAlert();
  const { isEditMode } = useEditSearchAlert();
  const {
    filledPreferences,
    filledPreferencesTrackingContext,
    filledPreferencesTrackingElementDetail,
  } = useSearchStateFilledJobPreferences();
  const updateJobPreferences = useUpdateJobPreferences();

  const trackNwt = useJobsNwtTracking();
  const location = useLocation();

  const handleOnCompleted: UpdateJobPreferencesMutationParams['onCompleted'] = (
    response
  ) => {
    if (!isValidResponse(response)) {
      return;
    }

    // User enabled the toggle and preferences submission
    const query = new URLSearchParams(location.search);
    trackNwt({
      context: filledPreferencesTrackingContext.join(', '),
      element: 'jobs_preferences_search_alert_on',
      elementDetail: filledPreferencesTrackingElementDetail.join(', '),
      event: 'clicked',
      eventSchema: 'searchresult',
      itemCount: itemCount,
      page: 'jobs_search/serp',
      query: getNwtCleanQuery(query, false),
      trackingToken: trackingToken,
    });
  };

  const handleOnDelete: UpdateJobPreferencesMutationParams['onCompleted'] = (
    response
  ) => {
    if (!isValidResponse(response)) {
      return;
    }

    // User disabled the toggle
    const query = new URLSearchParams(location.search);
    trackNwt({
      context: filledPreferencesTrackingContext.join(', '),
      element: 'jobs_preferences_search_alert_off',
      event: 'clicked',
      eventSchema: 'searchresult',
      itemCount: itemCount,
      page: 'jobs_search/serp',
      query: getNwtCleanQuery(query, false),
      trackingToken: trackingToken,
    });
  };

  const handleOnToggle = (): void => {
    onToggleAlert({
      onCreateCompleted: (data) => {
        if (
          data.createSearchAlert &&
          data.createSearchAlert.__typename === 'SearchAlertCreationSuccess'
        ) {
          updateJobPreferences({
            onCompleted: handleOnCompleted,
            ...getFillPreferencesInput(filledPreferences),
          });
        }
      },
      onDeleteCompleted: (data) => {
        if (
          data.deleteSearchAlert &&
          data.deleteSearchAlert.__typename === 'Empty'
        ) {
          // the user who sees this banner has no preferences set,
          // so we can reset everything
          updateJobPreferences({
            onCompleted: handleOnDelete,
            ...DEFAULT_DELETE_ARGS,
          });
        }
      },
      PropTrackActionData: {
        PropActionOrigin: `jobs_toggel_${isAlertActive ? 'off' : 'on'}`,
        ...(isAlertActive
          ? {
              PropTrackActionList:
                'jobs_delete_search_alert_completed;preferences_jobseeker_criteria_updated',
            }
          : {
              PropContextDimension4:
                filledPreferencesTrackingElementDetail.join(', '),
              PropContextDimension5:
                filledPreferencesTrackingContext.join(', '),
              PropTrackAction: 'jobs_search_alert_create_confirm',
              PropTrackActionList:
                'jobs_search_alert_create_confirm;preferences_jobseeker_criteria_updated',
            }),
      },
    });
  };

  if (isEditMode && !isAlertActive) {
    return (
      <Button variant="secondary" size="small" onClick={handleOnToggle}>
        <FormattedMessage id="JOBS_SEARCH_EDIT_ALERT_BANNER_SAVE_BUTTON" />
      </Button>
    );
  }

  return (
    <Toggle
      aria-pressed={isAlertActive}
      data-testid="job-preferences-search-alert-banner-action"
      disabled={isLoading}
      id={SEARCH_ALERT_JOB_PREFERENCES_LABEL_ID}
      on={isAlertActive}
      onChange={handleOnToggle}
      role="button"
    />
  );
};
